var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { handleSupabaseError } from "@cloudy/utils/common";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { thoughtKeys, thoughtQueryKeys } from "src/api/queryKeys";
import { supabase } from "src/clients/supabase";
import { fixOneToOne } from "src/utils";
import { useThought } from "./editor/hooks";
export var useAddDocumentUser = function (docId) {
    var queryClient = useQueryClient();
    return useMutation({
        mutationFn: function (email) { return __awaiter(void 0, void 0, void 0, function () {
            var user, _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _a = handleSupabaseError;
                        return [4 /*yield*/, supabase.from("users").select("id").eq("email", email).single()];
                    case 1:
                        user = _a.apply(void 0, [_c.sent()]);
                        if (!user) {
                            throw new Error("User not found");
                        }
                        _b = handleSupabaseError;
                        return [4 /*yield*/, supabase.from("document_shares").insert({
                                document_id: docId,
                                user_id: user.id,
                            })];
                    case 2: return [2 /*return*/, _b.apply(void 0, [_c.sent()])];
                }
            });
        }); },
        onSuccess: function () {
            queryClient.invalidateQueries({ queryKey: thoughtKeys.detail(docId) });
            queryClient.invalidateQueries({ queryKey: thoughtQueryKeys.sharedWith(docId) });
        },
    });
};
export var useRemoveDocumentUser = function (docId) {
    var queryClient = useQueryClient();
    return useMutation({
        mutationFn: function (userId) { return __awaiter(void 0, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = handleSupabaseError;
                        return [4 /*yield*/, supabase.from("document_shares").delete().match({ document_id: docId, user_id: userId })];
                    case 1: return [2 /*return*/, _a.apply(void 0, [_b.sent()])];
                }
            });
        }); },
        onSuccess: function () {
            queryClient.invalidateQueries({ queryKey: thoughtKeys.detail(docId) });
            queryClient.invalidateQueries({ queryKey: thoughtQueryKeys.sharedWith(docId) });
        },
    });
};
export var useDocumentAccessControl = function (docId) {
    var doc = useThought(docId).data;
    var users = useQuery({
        queryKey: thoughtQueryKeys.sharedWith(docId),
        queryFn: function () { return __awaiter(void 0, void 0, void 0, function () {
            var author, _a, _b, documentShares, _c, workspaceUsers, usersWithAccess;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        if (!doc) {
                            return [2 /*return*/, []];
                        }
                        if (!doc.author_id) return [3 /*break*/, 2];
                        _b = handleSupabaseError;
                        return [4 /*yield*/, supabase.from("users").select("id, name, email").eq("id", doc.author_id).single()];
                    case 1:
                        _a = _b.apply(void 0, [_d.sent()]);
                        return [3 /*break*/, 3];
                    case 2:
                        _a = null;
                        _d.label = 3;
                    case 3:
                        author = _a;
                        _c = handleSupabaseError;
                        return [4 /*yield*/, supabase
                                .from("document_shares")
                                .select("\n\t\t\t\t\tuser:users!user_id (\n\t\t\t\t\t  id, \n\t\t\t\t\t  name, \n\t\t\t\t\t  email,\n\t\t\t\t\t  workspace_users (\n\t\t\t\t\t\tid,\n\t\t\t\t\t\tworkspace_id\n\t\t\t\t\t  )\n\t\t\t\t\t)\n\t\t\t\t  ")
                                .eq("document_id", docId)];
                    case 4:
                        documentShares = _c.apply(void 0, [_d.sent()]);
                        workspaceUsers = new Set(documentShares === null || documentShares === void 0 ? void 0 : documentShares.flatMap(function (share) {
                            var _a;
                            var user = fixOneToOne(share.user);
                            return (_a = user === null || user === void 0 ? void 0 : user.workspace_users) === null || _a === void 0 ? void 0 : _a.filter(function (wu) { return wu.workspace_id === doc.workspace_id; }).map(function (wu) { return user.id; });
                        }));
                        usersWithAccess = __spreadArray(__spreadArray([], (author
                            ? [
                                __assign(__assign({}, author), { isExternal: false, isAuthor: true }),
                            ]
                            : []), true), documentShares === null || documentShares === void 0 ? void 0 : documentShares.map(function (share) {
                            var _a;
                            return (__assign(__assign({}, fixOneToOne(share.user)), { isExternal: !workspaceUsers.has((_a = fixOneToOne(share.user)) === null || _a === void 0 ? void 0 : _a.id) }));
                        }), true);
                        return [2 /*return*/, usersWithAccess];
                }
            });
        }); },
    }).data;
    var accessStrategy = doc === null || doc === void 0 ? void 0 : doc.access_strategy;
    return useMemo(function () { return ({
        accessStrategy: accessStrategy,
        users: users !== null && users !== void 0 ? users : [],
    }); }, [accessStrategy, users]);
};
